/**
 * Tahmin Etkinlikleri (/admin/tahmin-etkinlikleri).
 *
 * Skor tahmin yarışmasının paneli: etkinlik açılır, bağlantısı Telegram
 * postuna konur, maç bitince sonuç girilir ve doğru bilenler listelenir.
 * Kuralların kapısı sunucudadır (services/prediction.py) — kapanış sunucu
 * saatiyle uygulanır, üye başına tek tahmin vardır ve gönderilen tahmin
 * değiştirilemez; bu ekran yalnızca yönetir ve kanıtı gösterir.
 *
 * Görsel dil Çekilişler sayfasınınkiyle aynıdır (raffle.css).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Target, Plus, Edit3, Trash2, X, Loader2, Search, RefreshCw, Trophy,
  Users, Gift, Coins, Sparkles, CalendarClock, ShieldCheck, ArrowLeft,
  CheckCircle2, ChevronLeft, ChevronRight, AlertTriangle, Info, Copy,
  Download, Link2, Send, MessageSquare, Lock, Eye, EyeOff, Clock,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import PageSizeSelect from './components/PageSizeSelect';
import { readPageSize, writePageSize } from './lib/pagination';
import {
  PHASE_LABELS, SOCIAL_LABELS, WIN_RULE_LABELS, eventLink,
} from '../lib/prediction';
import './raffle.css';

// Yalnızca varsayılan: boyutu yönetici seçer (bkz. lib/pagination).
const PAGE_SIZE = 50;

const emptyEvent = {
  kind: 'score',
  title: '',
  slug: '',
  description: '',
  image_url: '',
  home_team: '',
  away_team: '',
  match_at: '',
  closes_at: '',
  answer_mode: 'none',
  answer_label: '',
  social_platform: '',
  social_required: false,
  require_telegram: false,
  require_kyc: false,
  win_rule: 'exact',
  reward_type: 'none',
  reward_amount: 0,
  wager_multiplier: 0,
  note: '',
  status: 'draft',
};

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

const compact = (value) => new Intl.NumberFormat('tr-TR').format(Number(value || 0));

const formatDate = (value) => (value ? new Date(value).toLocaleString('tr-TR') : '—');

// datetime-local alanı yerel saat bekler, toISOString() ise UTC verir;
// doldururken yerele çevrilmezse tarih her kayıtta UTC farkı kadar kayar.
const toInputDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toPayloadDate = (value) => (value ? new Date(value).toISOString() : null);

const PHASE_TONES = {
  draft: 'is-muted', open: 'is-ok', closed: 'is-warn', settled: 'is-info',
};

const errorText = (requestError) => {
  const detail = requestError.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join(', ');
  return detail || 'İşlem tamamlanamadı';
};

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const Predictions = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [publicActive, setPublicActive] = useState(false);
  const [siteBase, setSiteBase] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyEvent);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get('/admin/predictions');
      setItems(data.items || []);
      setSummary(data.summary || null);
      setPublicActive(data.public_active === true);
      setSiteBase(data.site_base || window.location.origin);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(emptyEvent);
    setModal({ mode: 'new' });
  };

  const openEdit = (item) => {
    setForm({
      ...emptyEvent,
      ...item,
      description: item.description || '',
      match_at: toInputDate(item.match_at),
      closes_at: toInputDate(item.closes_at),
    });
    setModal({ mode: 'edit', id: item.id, entries: item.entry_count || 0 });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        kind: form.kind,
        title: form.title.trim(),
        slug: form.slug.trim(),
        description: form.description || '',
        image_url: form.image_url || '',
        home_team: form.kind === 'score' ? form.home_team.trim() : '',
        away_team: form.kind === 'score' ? form.away_team.trim() : '',
        match_at: toPayloadDate(form.match_at),
        closes_at: toPayloadDate(form.closes_at),
        // Skor tahmini yalnızca rakamdır; metin alanı sunucuda da kapalıdır.
        answer_mode: form.kind === 'question' ? 'required' : 'none',
        answer_label: form.kind === 'question' ? form.answer_label.trim() : '',
        social_platform: form.social_platform,
        social_required: !!form.social_required,
        require_telegram: !!form.require_telegram,
        require_kyc: !!form.require_kyc,
        win_rule: form.win_rule,
        reward_type: form.reward_type,
        reward_amount: Number(form.reward_amount || 0),
        wager_multiplier: Number(form.wager_multiplier || 0),
        note: form.note || '',
        status: form.status,
      };
      if (modal.mode === 'new') {
        if (!payload.slug) delete payload.slug;
        const { data } = await client.post('/admin/predictions', payload);
        setModal(null);
        await load();
        setSelectedId(data.id);
      } else {
        if (!payload.slug) delete payload.slug;
        await client.patch(`/admin/predictions/${modal.id}`, payload);
        setModal(null);
        await load();
      }
      toast({ title: 'Etkinlik kaydedildi' });
    } catch (requestError) {
      toast({ title: 'Kaydedilemedi', description: errorText(requestError) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(
      `${item.title} silinsin mi? Gönderilmiş ${item.entry_count || 0} tahmin de silinir.`)) return;
    try {
      await client.delete(`/admin/predictions/${item.id}`);
      if (selectedId === item.id) setSelectedId(null);
      await load();
      toast({ title: 'Etkinlik silindi' });
    } catch (requestError) {
      toast({ title: 'Silinemedi', description: errorText(requestError) });
    }
  };

  const modalView = modal && (
    <EventModal
      modal={modal}
      form={form}
      setForm={setForm}
      busy={busy}
      onClose={() => setModal(null)}
      onSave={save}
    />
  );

  if (selectedId) {
    return (
      <>
        <PredictionDetail
          eventId={selectedId}
          siteBase={siteBase}
          onBack={() => { setSelectedId(null); load(); }}
          onEdit={openEdit}
          editingOpen={Boolean(modal)}
        />
        {modalView}
      </>
    );
  }

  return (
    <div className="rfl-page">
      <header className="rfl-head">
        <span className="rfl-head-icon"><Target size={21} /></span>
        <div className="rfl-head-text">
          <h1>
            Tahmin Etkinlikleri
            <span className={`rfl-pill ${publicActive ? 'is-ok' : 'is-muted'}`}>
              {publicActive ? <Eye size={11} /> : <EyeOff size={11} />}
              {publicActive ? 'Menüde görünüyor' : 'Menüde yok'}
            </span>
          </h1>
          <p>
            Skor tahmin yarışmaları. Üye tahminini kendi hesabıyla ve maç
            başlamadan <b>bir kez</b> gönderir; kapanışı sunucu uygular ve
            gönderilen tahmin değiştirilemez. Bağlantıyı Telegram postuna koyun.
          </p>
        </div>
        <div className="rfl-head-actions">
          <button type="button" onClick={load} className="admin-btn-secondary">
            <RefreshCw size={14} /> Yenile
          </button>
          <button type="button" onClick={openNew} className="admin-btn-primary">
            <Plus size={14} /> Yeni Etkinlik
          </button>
        </div>
      </header>

      <div className="rfl-stats">
        <Stat icon={Clock} tone="ok" label="Tahmin alıyor" value={compact(summary?.open ?? 0)}
          hint={`Toplam ${compact(summary?.total ?? items.length)} etkinlik`} />
        <Stat icon={Trophy} tone={summary?.awaiting_result ? 'warn' : 'neutral'} label="Sonuç bekleyen"
          value={compact(summary?.awaiting_result ?? 0)} hint="Maçı biten, sonucu girilmemiş" />
        <Stat icon={Users} tone="info" label="Toplam tahmin" value={compact(summary?.entries ?? 0)} />
        <Stat icon={Gift} tone={summary?.unpaid_winners ? 'warn' : 'neutral'} label="Ödül bekleyen"
          value={compact(summary?.unpaid_winners ?? 0)} hint="Ödülü dağıtılmamış kazanan" />
      </div>

      {loading ? (
        <div className="rfl-skeleton"><span /><span /><span /></div>
      ) : error ? (
        <div className="rfl-panel">
          <div className="rfl-state">
            <AlertTriangle size={26} />
            <h2>Etkinlikler alınamadı</h2>
            <p>{error}</p>
            <button type="button" onClick={load} className="admin-btn-secondary">
              <RefreshCw size={14} /> Yeniden dene
            </button>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="rfl-panel">
          <div className="rfl-state">
            <Target size={26} />
            <h2>Henüz etkinlik oluşturulmadı</h2>
            <p>
              Maçı ve kapanış saatini girin, etkinliği yayına alın ve
              bağlantısını Telegram postunun altına koyun.
            </p>
            <button type="button" onClick={openNew} className="admin-btn-primary">
              <Plus size={14} /> Yeni Etkinlik
            </button>
          </div>
        </div>
      ) : (
        <div className="rfl-grid">
          {items.map((item) => (
            <article key={item.id} className={`rfl-card ${item.phase === 'draft' ? 'is-draft' : ''}`}>
              <div className="rfl-card-body">
                <div className="flex flex-wrap gap-1.5">
                  <span className={`rfl-pill ${PHASE_TONES[item.phase] || 'is-muted'}`}>
                    {PHASE_LABELS[item.phase] || item.phase}
                  </span>
                  <span className="rfl-pill is-muted">
                    {item.kind === 'score' ? 'Skor tahmini' : 'Serbest soru'}
                  </span>
                  {item.reward_type !== 'none' && (
                    <span className="rfl-pill is-ok"><Coins size={11} /> {money(item.reward_amount)}</span>
                  )}
                </div>
                <button type="button" className="rfl-card-title" onClick={() => setSelectedId(item.id)}>
                  {item.title}
                </button>
                {item.kind === 'score' && (
                  <p className="rfl-card-desc">{item.home_team} – {item.away_team}</p>
                )}
                <div className="rfl-card-facts">
                  <div className="rfl-fact">
                    <span className="rfl-fact-label">Tahmin</span>
                    <span className="rfl-fact-value">{compact(item.entry_count)}</span>
                  </div>
                  <div className="rfl-fact is-ok">
                    <span className="rfl-fact-label">Kazanan</span>
                    <span className="rfl-fact-value">
                      {item.phase === 'settled' ? compact(item.winner_count) : '—'}
                    </span>
                  </div>
                  {item.phase === 'settled' && item.kind === 'score' && (
                    <div className="rfl-fact is-gold">
                      <span className="rfl-fact-label">Sonuç</span>
                      <span className="rfl-fact-value">{item.result_home} - {item.result_away}</span>
                    </div>
                  )}
                </div>
                <div className="rfl-card-window">
                  <CalendarClock size={13} /> Kapanış {formatDate(item.closes_at)}
                </div>
              </div>
              <div className="rfl-card-foot">
                <button type="button" className="rfl-mini is-primary is-grow" onClick={() => setSelectedId(item.id)}>
                  <Trophy size={13} /> Yönet
                </button>
                <button
                  type="button"
                  className="rfl-mini is-icon"
                  title="Bağlantıyı kopyala"
                  onClick={async () => {
                    const ok = await copyText(eventLink(siteBase, item.slug));
                    toast({ title: ok ? 'Bağlantı kopyalandı' : 'Kopyalanamadı' });
                  }}
                >
                  <Copy size={13} />
                </button>
                <button
                  type="button"
                  className="rfl-mini is-icon"
                  onClick={() => openEdit(item)}
                  disabled={item.phase === 'settled'}
                  title={item.phase === 'settled' ? 'Sonuçlanmış etkinlik değiştirilemez' : 'Düzenle'}
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  className="rfl-mini is-icon is-danger"
                  onClick={() => remove(item)}
                  disabled={item.paid_count > 0}
                  title={item.paid_count > 0 ? 'Ödül dağıtılmış etkinlik silinemez' : 'Sil'}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {modalView}
    </div>
  );
};

/** Etkinlik formu — ekran görüntüsündeki alan kümesinin karşılığı. */
const EventModal = ({ modal, form, setForm, busy, onClose, onSave }) => {
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));
  const score = form.kind === 'score';
  const locked = modal.mode === 'edit' && modal.entries > 0;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />
      <div className="relative admin-card admin-modal w-full max-w-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="admin-modal-head">
          <div className="admin-modal-icon"><Target size={19} /></div>
          <div className="min-w-0">
            <h2 className="font-black text-[15px] leading-tight">
              {modal.mode === 'new' ? 'Yeni Tahmin Etkinliği' : 'Etkinliği Düzenle'}
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Maç, kapanış ve katılım şartları. Sonuç maç bittikten sonra girilir.
            </p>
          </div>
          <button type="button" onClick={onClose} className="icon-btn view ml-auto"><X size={14} /></button>
        </div>

        <div className="admin-modal-body">
          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><Sparkles size={12} /> Etkinlik</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4">
              <Choice
                title="Skor Tahmini"
                description="Ev – deplasman skoru; kazananlar sonuçla eşleşir"
                active={score}
                disabled={locked}
                onSelect={() => set({ kind: 'score', answer_mode: 'none' })}
              />
              <Choice
                title="Serbest Soru"
                description="Metin cevap; doğru cevabı yazınca kazananlar eşleşir"
                active={!score}
                disabled={locked}
                onSelect={() => set({ kind: 'question', answer_mode: 'required' })}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Başlık" hint="Oyuncunun sayfada ve menüde göreceği ad.">
                  <input value={form.title} onChange={(event) => set({ title: event.target.value })}
                    className="admin-input" placeholder="Derbi Skor Tahmini" />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field
                  label="Adres"
                  hint={locked
                    ? 'Tahmin gelmiş etkinliğin adresi değiştirilemez — paylaşılan bağlantı kırılırdı.'
                    : 'Boş bırakılırsa başlıktan üretilir: /etkinlik/derbi-skor-tahmini'}
                >
                  <input value={form.slug} disabled={locked}
                    onChange={(event) => set({ slug: event.target.value })}
                    className="admin-input" placeholder="derbi-skor-tahmini" />
                </Field>
              </div>
              {score && (
                <>
                  <Field label="Ev sahibi">
                    <input value={form.home_team} onChange={(event) => set({ home_team: event.target.value })}
                      className="admin-input" placeholder="Galatasaray" />
                  </Field>
                  <Field label="Deplasman">
                    <input value={form.away_team} onChange={(event) => set({ away_team: event.target.value })}
                      className="admin-input" placeholder="Fenerbahçe" />
                  </Field>
                </>
              )}
              <div className="sm:col-span-2">
                <Field label={score ? 'Açıklama' : 'Açıklama / Soru'} hint="Oyuncuya gösterilen metin (düz metin).">
                  <textarea value={form.description} rows={3} className="admin-input"
                    onChange={(event) => set({ description: event.target.value })}
                    placeholder={score ? 'Maç başlamadan skoru tahmin edin.' : 'Maçın ilk golünü kim atar?'} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Görsel adresi" hint="İsteğe bağlı. 16:7 oranında bir kapak görseli.">
                  <input value={form.image_url} onChange={(event) => set({ image_url: event.target.value })}
                    className="admin-input" placeholder="https://… veya /api/uploads/…" />
                </Field>
              </div>
            </div>
          </section>

          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><CalendarClock size={12} /> Zaman</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Maç saati" hint="Oyuncuya ilan edilir.">
                <input type="datetime-local" value={form.match_at}
                  onChange={(event) => set({ match_at: event.target.value })} className="admin-input" />
              </Field>
              <Field label="Son tahmin zamanı" hint="Boşsa maç saatidir. Maç saatinden sonra olamaz.">
                <input type="datetime-local" value={form.closes_at}
                  onChange={(event) => set({ closes_at: event.target.value })} className="admin-input" />
              </Field>
            </div>
          </section>

          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><MessageSquare size={12} /> Oyuncudan istenenler</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {score ? (
                <div className="sm:col-span-2 rfl-note is-info">
                  <Info size={15} />
                  <span className="rfl-note-body">
                    Oyuncu yalnızca iki skor kutusunu <b>rakamla</b> doldurur; metin alanı açılmaz.
                    Kazananlar girilen sonuçla otomatik eşleşir.
                  </span>
                </div>
              ) : (
                <Field label="Soru" hint="Boşsa açıklama soru sayılır.">
                  <input value={form.answer_label} onChange={(event) => set({ answer_label: event.target.value })}
                    className="admin-input" placeholder="İlk golü kim atar?" />
                </Field>
              )}
              <Field label="Sosyal medya" hint="Oyuncudan kullanıcı adı istenir; takip doğrulanmaz.">
                <select value={form.social_platform}
                  onChange={(event) => set({ social_platform: event.target.value })} className="admin-input">
                  <option value="">İstenmez</option>
                  {Object.entries(SOCIAL_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="grid gap-2.5 mt-3">
              {form.social_platform && (
                <SwitchRow icon={MessageSquare} label="Sosyal medya kullanıcı adı zorunlu"
                  description="Kapalıyken alan isteğe bağlıdır."
                  checked={!!form.social_required} onChange={(social_required) => set({ social_required })} />
              )}
              <SwitchRow icon={Send} label="Telegram topluluğu üyeliği şartı"
                description="Oyuncunun Telegram hesabı bağlı ve topluluk grubunda olmalıdır; her gönderimde yeniden sorulur."
                checked={!!form.require_telegram} onChange={(require_telegram) => set({ require_telegram })} />
              <SwitchRow icon={ShieldCheck} label="Kimlik doğrulaması şartı"
                description="Yalnızca KYC'si onaylı üyeler tahmin gönderebilir."
                checked={!!form.require_kyc} onChange={(require_kyc) => set({ require_kyc })} />
            </div>
          </section>

          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><Gift size={12} /> Kazanma ve ödül</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {score && (
                <Field label="Kazanma kuralı" hint="Birden çok doğru bilen olursa hepsi kazanır.">
                  <select value={form.win_rule} onChange={(event) => set({ win_rule: event.target.value })}
                    className="admin-input">
                    {Object.entries(WIN_RULE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </Field>
              )}
              <Field label="Ödül" hint="Her kazanana aynı tutar; dağıtım ayrı bir düğmedir.">
                <select value={form.reward_type} onChange={(event) => set({ reward_type: event.target.value })}
                  className="admin-input">
                  <option value="none">Ödül yok (yalnızca liste)</option>
                  <option value="cash">Nakit (çevrimsiz)</option>
                  <option value="bonus">Bonus</option>
                </select>
              </Field>
              {form.reward_type !== 'none' && (
                <Field label="Kazanan başına tutar">
                  <Affix text="₺">
                    <input type="number" min="0" step="1" value={form.reward_amount}
                      onChange={(event) => set({ reward_amount: event.target.value })} className="admin-input pr-10" />
                  </Affix>
                </Field>
              )}
              {form.reward_type === 'bonus' && (
                <Field label="Çevrim çarpanı" hint="0 = çevrimsiz (bonus ana bakiyeye yazılır).">
                  <Affix text="×">
                    <input type="number" min="0" step="1" value={form.wager_multiplier}
                      onChange={(event) => set({ wager_multiplier: event.target.value })} className="admin-input pr-10" />
                  </Affix>
                </Field>
              )}
            </div>
          </section>

          <section className="admin-fieldset">
            <h3 className="admin-fieldset-title"><ShieldCheck size={12} /> Durum</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <Choice title="Taslak" description="Oyuncuya görünmez" active={form.status === 'draft'}
                onSelect={() => set({ status: 'draft' })} />
              <Choice title="Yayında" description="Bağlantı açılır, tahmin alınır" active={form.status === 'active'}
                onSelect={() => set({ status: 'active' })} />
            </div>
            <div className="mt-4">
              <Field label="Not" hint="Yalnızca panelde görünür.">
                <input value={form.note} onChange={(event) => set({ note: event.target.value })}
                  className="admin-input" maxLength={255} />
              </Field>
            </div>
          </section>
        </div>

        <div className="admin-modal-foot">
          <span className="admin-modal-summary">
            <Target size={12} />
            <b className="truncate">{form.title.trim() || 'Etkinlik'}</b>
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="admin-btn-secondary">Vazgeç</button>
            <button type="button" disabled={busy || !form.title.trim()} onClick={onSave} className="admin-btn-primary">
              {busy && <Loader2 size={14} className="animate-spin" />} Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Tek etkinliğin yönetimi: bağlantı, sonuç, kazananlar ve tahmin listesi. */
const PredictionDetail = ({ eventId, siteBase, onBack, onEdit, editingOpen }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(
    () => readPageSize('prediction-entries', PAGE_SIZE));
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState({ home: '', away: '', note: '', answer: '' });
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await client.get(`/admin/predictions/${eventId}`);
      setDetail(data);
      setResult({
        home: data.result_home ?? '',
        away: data.result_away ?? '',
        note: data.result_note || '',
        answer: data.result_answer || '',
      });
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const loadEntries = useCallback(async () => {
    try {
      const { data } = await client.get(`/admin/predictions/${eventId}/entries`, {
        params: { search, only, skip: page * pageSize, limit: pageSize },
      });
      setEntries(data);
    } catch {
      setEntries({ items: [], total: 0 });
    }
  }, [eventId, search, only, page, pageSize]);

  // Düzenleme penceresi kapanınca kayıt yeniden okunur (değişen kapanış vb.).
  useEffect(() => { if (!editingOpen) load(); }, [load, editingOpen]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const run = async (key, action, title) => {
    setBusy(key);
    try {
      const description = await action();
      await load();
      await loadEntries();
      toast({ title, description });
    } catch (requestError) {
      toast({ title: 'İşlem tamamlanamadı', description: errorText(requestError) });
    } finally {
      setBusy('');
    }
  };

  const settle = () => run('settle', async () => {
    const body = { result_note: result.note };
    if (detail.kind === 'score') {
      body.result_home = Number(result.home);
      body.result_away = Number(result.away);
    } else {
      body.result_answer = result.answer.trim();
    }
    const { data } = await client.post(`/admin/predictions/${eventId}/settle`, body);
    return `${data.winner_count} kazanan`;
  }, 'Sonuç kaydedildi');

  const distribute = () => {
    if (!window.confirm(
      `Ödül dağıtılsın mı? Her kazanana ${money(detail.reward_amount)} yazılır. `
      + 'Dağıtım başladıktan sonra sonuç değiştirilemez.')) return;
    run('distribute', async () => {
      const { data } = await client.post(`/admin/predictions/${eventId}/distribute`);
      let text = `${data.paid} kazanana ödendi`;
      if (data.skipped) text += ` · ${data.skipped} yasaklı hesap atlandı`;
      if (data.remaining) text += ` · ${data.remaining} kaldı, tekrar basın`;
      return text;
    }, 'Ödül dağıtıldı');
  };

  const toggleWinner = (entry) => run(`w-${entry.id}`, async () => {
    await client.post(`/admin/predictions/${eventId}/entries/${entry.id}/winner`, {
      is_winner: !entry.is_winner,
    });
    return entry.username;
  }, entry.is_winner ? 'Kazanan kaldırıldı' : 'Kazanan işaretlendi');

  const exportCsv = async () => {
    setBusy('export');
    try {
      const response = await client.get(`/admin/predictions/${eventId}/entries/export`, {
        params: { only }, responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `tahmin-${detail.slug}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (requestError) {
      toast({ title: 'CSV indirilemedi', description: errorText(requestError) });
    } finally {
      setBusy('');
    }
  };

  if (loading && !detail) {
    return (
      <div className="rfl-page">
        <div className="rfl-panel"><div className="rfl-state"><Loader2 size={24} className="animate-spin" /></div></div>
      </div>
    );
  }
  if (error || !detail) {
    return (
      <div className="rfl-page">
        <div className="rfl-panel">
          <div className="rfl-state">
            <AlertTriangle size={26} />
            <h2>Etkinlik açılamadı</h2>
            <p>{error}</p>
            <button type="button" onClick={onBack} className="admin-btn-secondary">
              <ArrowLeft size={14} /> Listeye dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  const score = detail.kind === 'score';
  const settled = detail.phase === 'settled';
  const paid = detail.paid_count > 0;
  const link = eventLink(siteBase, detail.slug);
  const unpaid = detail.winner_count - detail.paid_count;
  const hasReward = detail.reward_type !== 'none' && Number(detail.reward_amount) > 0;
  const pages = Math.max(1, Math.ceil((entries.total || 0) / pageSize));
  const canSettle = detail.phase === 'closed' || (settled && !paid);
  const resultReady = !score || (result.home !== '' && result.away !== '');

  return (
    <div className="rfl-page">
      <header className="rfl-head">
        <button type="button" onClick={onBack} className="rfl-head-icon" title="Listeye dön">
          <ArrowLeft size={19} />
        </button>
        <div className="rfl-head-text">
          <h1>
            {detail.title}
            <span className={`rfl-pill ${PHASE_TONES[detail.phase] || 'is-muted'}`}>
              {PHASE_LABELS[detail.phase] || detail.phase}
            </span>
          </h1>
          <p>
            {score ? `${detail.home_team} – ${detail.away_team} · ` : 'Serbest soru · '}
            Maç {formatDate(detail.match_at)} · Kapanış {formatDate(detail.closes_at)}
            {score ? ` · ${WIN_RULE_LABELS[detail.win_rule]}` : ''}
          </p>
        </div>
        <div className="rfl-head-actions">
          {!settled && (
            <button type="button" onClick={() => onEdit(detail)} className="admin-btn-secondary">
              <Edit3 size={14} /> Düzenle
            </button>
          )}
        </div>
      </header>

      <section className="rfl-panel">
        <div className="rfl-panel-head">
          <span className="rfl-panel-title"><Link2 size={14} /> Paylaşılacak bağlantı</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 p-4">
          <input readOnly value={link} className="admin-input flex-1 min-w-0" onFocus={(event) => event.target.select()} />
          <button
            type="button"
            className="admin-btn-primary"
            onClick={async () => {
              const ok = await copyText(link);
              toast({ title: ok ? 'Bağlantı kopyalandı' : 'Kopyalanamadı' });
            }}
          >
            <Copy size={14} /> Kopyala
          </button>
        </div>
        {detail.phase === 'draft' && (
          <div className="rfl-note is-warn mx-4 mb-4">
            <AlertTriangle size={15} />
            <span className="rfl-note-body">
              Etkinlik <b>taslak</b>: bağlantı oyuncuya açılmaz. Paylaşmadan önce
              <b> Düzenle → Yayında</b> yapın.
            </span>
          </div>
        )}
      </section>

      <div className="rfl-stats">
        <Stat icon={Users} tone="info" label="Tahmin" value={compact(detail.entry_count)} />
        <Stat icon={Trophy} tone="ok" label="Kazanan" value={settled ? compact(detail.winner_count) : '—'} />
        <Stat icon={Coins} tone={hasReward && unpaid > 0 && settled ? 'warn' : 'neutral'} label="Ödenen"
          value={hasReward ? compact(detail.paid_count) : '—'}
          hint={hasReward ? money(detail.paid_amount) : 'Ödül tanımlı değil'} />
        <Stat icon={Lock} tone="neutral" label="Kapanış" value={formatDate(detail.closes_at)}
          hint="Sunucu saatiyle uygulanır" />
      </div>

      {score && (detail.score_breakdown || []).length > 0 && (
        <section className="rfl-panel">
          <div className="rfl-panel-head">
            <span className="rfl-panel-title"><Target size={14} /> En çok tahmin edilen skorlar</span>
          </div>
          <div className="flex flex-wrap gap-2 p-4">
            {detail.score_breakdown.map((row) => (
              <span key={`${row.home}-${row.away}`} className="rfl-pill is-muted">
                {row.home} - {row.away} · {compact(row.count)}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="rfl-panel">
        <div className="rfl-panel-head">
          <span className="rfl-panel-title"><Trophy size={14} /> Sonuç</span>
          {settled && (
            <span className="rfl-panel-meta">
              {formatDate(detail.settled_at)} · {detail.settled_by || '—'}
            </span>
          )}
        </div>
        <div className="p-4 grid gap-3">
          {detail.phase === 'open' || detail.phase === 'draft' ? (
            <div className="rfl-note is-info">
              <Info size={15} />
              <span className="rfl-note-body">
                Sonuç, tahmin süresi dolduktan sonra girilir ({formatDate(detail.closes_at)}).
              </span>
            </div>
          ) : (
            <>
              {paid && (
                <div className="rfl-note is-info">
                  <Lock size={15} />
                  <span className="rfl-note-body">
                    Ödül dağıtımı başladı; sonuç artık değiştirilemez.
                  </span>
                </div>
              )}
              <div className="flex flex-wrap items-end gap-3">
                {score && (
                  <>
                    <Field label={detail.home_team}>
                      <input type="number" min="0" max="99" value={result.home} disabled={!canSettle}
                        onChange={(event) => setResult({ ...result, home: event.target.value })}
                        className="admin-input admin-input-w-xs" />
                    </Field>
                    <Field label={detail.away_team}>
                      <input type="number" min="0" max="99" value={result.away} disabled={!canSettle}
                        onChange={(event) => setResult({ ...result, away: event.target.value })}
                        className="admin-input admin-input-w-xs" />
                    </Field>
                  </>
                )}
                {!score && (
                  <div className="flex-1 min-w-[200px]">
                    <Field label="Doğru cevap" hint="Birden çok kabul edilen cevap varsa virgülle ayırın.">
                      <input value={result.answer} disabled={!canSettle}
                        onChange={(event) => setResult({ ...result, answer: event.target.value })}
                        className="admin-input" maxLength={255} placeholder="4" />
                    </Field>
                  </div>
                )}
                <div className="flex-1 min-w-[200px]">
                  <Field label="Sonuç notu" hint="Oyuncu sayfasında kazananların üstünde görünür.">
                    <input value={result.note} disabled={!canSettle}
                      onChange={(event) => setResult({ ...result, note: event.target.value })}
                      className="admin-input" maxLength={255} />
                  </Field>
                </div>
                {canSettle && (
                  <button type="button" className="admin-btn-primary" disabled={busy === 'settle' || !resultReady}
                    onClick={settle}>
                    {busy === 'settle' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    {settled ? 'Sonucu Güncelle' : 'Sonucu Kaydet'}
                  </button>
                )}
              </div>
              {!score && (
                <p className="text-[11px] text-slate-500">
                  Doğru cevabı verenler skor tahminindeki gibi topluca kazanan olur; büyük/küçük harf,
                  Türkçe karakter ve noktalama farkı gözetilmez (“İcardi!” = “icardi”). Farklı yazılmış
                  cevapları aşağıdaki listeden elle işaretleyebilirsiniz; sonucu güncellemek elle verilen kararı bozmaz.
                </p>
              )}
              {settled && hasReward && unpaid > 0 && (
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" className="admin-btn-primary" disabled={busy === 'distribute'}
                    onClick={distribute}>
                    {busy === 'distribute' ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
                    Ödülü Dağıt ({compact(unpaid)} × {money(detail.reward_amount)})
                  </button>
                  <span className="text-[11px] text-slate-500">
                    Her kazanana bir kez yazılır; tekrar basmak ikinci ödeme yapmaz.
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="rfl-panel">
        <div className="rfl-toolbar">
          <label className="rfl-search">
            <Search size={14} />
            <input value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }}
              className="admin-input pl-10" placeholder="Kullanıcı adı ara…" />
          </label>
          <select value={only} onChange={(event) => { setOnly(event.target.value); setPage(0); }}
            className="admin-input admin-input-w-md">
            <option value="all">Tüm tahminler</option>
            <option value="winners">Kazananlar</option>
            <option value="unpaid">Ödülü verilmemiş kazananlar</option>
          </select>
          <span className="rfl-panel-meta">{compact(entries.total || 0)} kayıt</span>
          <button type="button" className="rfl-mini" onClick={exportCsv} disabled={busy === 'export'}>
            {busy === 'export' ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} CSV
          </button>
        </div>

        {entries.items.length === 0 ? (
          <div className="rfl-state">
            <Users size={24} />
            <h2>Kayıt yok</h2>
            <p>Bağlantıyı paylaşın; üyelerin tahminleri burada gönderim sırasıyla listelenir.</p>
          </div>
        ) : (
          <>
            <div className="rfl-scroll">
              <table className="rfl-table">
                <thead>
                  <tr>
                    <th>Üye</th>
                    {score && <th>Tahmin</th>}
                    {detail.answer_mode !== 'none' && <th>Cevap</th>}
                    {detail.social_platform && <th>{SOCIAL_LABELS[detail.social_platform]}</th>}
                    <th>Zaman</th>
                    <th>Durum</th>
                    <th className="rfl-cell-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.items.map((entry) => (
                    <tr key={entry.id}>
                      <td data-label="Üye">
                        <span className="rfl-user">
                          <span className="rfl-avatar">{(entry.username || '?').slice(0, 2).toUpperCase()}</span>
                          <span>
                            {entry.username}
                            {entry.ip && <span className="block text-[10px] text-slate-500">{entry.ip}</span>}
                          </span>
                        </span>
                      </td>
                      {score && (
                        <td data-label="Tahmin"><span className="rfl-count">{entry.home_score} - {entry.away_score}</span></td>
                      )}
                      {detail.answer_mode !== 'none' && (
                        <td data-label="Cevap" className="max-w-[260px] break-words">{entry.answer || '—'}</td>
                      )}
                      {detail.social_platform && (
                        <td data-label="Sosyal">{entry.social_handle ? `@${entry.social_handle}` : '—'}</td>
                      )}
                      <td data-label="Zaman">
                        <span className="rfl-mono">{formatDate(entry.created_at)}</span>
                        {entry.before_close && (
                          <span className="block text-[10px] text-emerald-400">Kapanıştan önce</span>
                        )}
                      </td>
                      <td data-label="Durum">
                        {entry.is_winner ? (
                          <span className="rfl-pill is-ok">
                            <Trophy size={11} /> Kazandı{entry.winner_source === 'manual' ? ' (elle)' : ''}
                          </span>
                        ) : settled ? (
                          <span className="rfl-pill is-muted">Kaybetti{entry.winner_source === 'manual' ? ' (elle)' : ''}</span>
                        ) : (
                          <span className="rfl-pill is-muted">Bekliyor</span>
                        )}
                        {entry.reward_status === 'delivered' && (
                          <span className="rfl-pill is-info ml-1"><Coins size={11} /> {money(entry.reward_amount)}</span>
                        )}
                      </td>
                      <td className="rfl-cell-right">
                        {settled && entry.reward_status !== 'delivered' && (
                          <button type="button" className={`rfl-mini ${entry.is_winner ? 'is-danger' : 'is-primary'}`}
                            disabled={busy === `w-${entry.id}`} onClick={() => toggleWinner(entry)}>
                            {entry.is_winner ? 'Kazananı kaldır' : 'Kazanan yap'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rfl-pager">
              {pages > 1 && (
                <>
                  <button type="button" className="rfl-mini is-icon" disabled={page === 0}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}>
                    <ChevronLeft size={13} />
                  </button>
                  <span>{page + 1} / {pages}</span>
                  <button type="button" className="rfl-mini is-icon" disabled={page + 1 >= pages}
                    onClick={() => setPage((current) => current + 1)}>
                    <ChevronRight size={13} />
                  </button>
                </>
              )}
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => {
                  setPage(0);
                  setPageSize(size);
                  writePageSize('prediction-entries', size);
                }}
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, hint, tone = 'neutral' }) => (
  <div className={`rfl-stat tone-${tone} ${tone === 'neutral' ? '' : 'is-toned'}`}>
    <span className="rfl-stat-label"><Icon size={12} /> {label}</span>
    <span className="rfl-stat-value">{value}</span>
    {hint && <span className="rfl-stat-hint">{hint}</span>}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="text-xs text-slate-400 block mb-1.5">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[11px] leading-relaxed text-slate-500">{hint}</span>}
  </label>
);

// Girişin içindeki birim etiketi; dolgu .admin-input.pr-10 ile verilir.
const Affix = ({ text, children }) => (
  <span className="admin-affix block">
    {children}
    <span className="admin-affix-text">{text}</span>
  </span>
);

const Choice = ({ title, description, active, onSelect, disabled }) => (
  <button type="button" onClick={onSelect} disabled={disabled && !active}
    className={`admin-choice ${active ? 'active' : ''}`}>
    <span className="min-w-0">
      <span className="block text-sm font-bold">{title}</span>
      <span className="block text-[11px] text-slate-500 mt-0.5">{description}</span>
    </span>
  </button>
);

const SwitchRow = ({ icon: Icon, label, description, checked, onChange }) => (
  <label className={`admin-switch ${checked ? 'on' : ''}`}>
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    <span className="admin-switch-track" aria-hidden="true" />
    <span className="min-w-0">
      <span className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={13} /> {label}</span>
      <span className="block text-[11px] leading-relaxed text-slate-500 mt-0.5">{description}</span>
    </span>
  </label>
);

export default Predictions;
